import React, { useState } from "react";
import Sidebar from "../Common/Sidebar";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useNavigate } from "react-router-dom";

export default function GuideLandingPage() {
  const [showWideSidebar, setShowWideSidebar] = useState(true);
   const navigate = useNavigate(); 

  const onSidebarValueChanged = (value: any) => {
    setShowWideSidebar(value);
  };
  
  return (
    <div className="d-flex vh-100 bg-light">
      <Sidebar pagename="guide" onSidebarValueChanged={onSidebarValueChanged} />

      <div  className="flex-grow-1 d-flex flex-column bg-light p-2 mt-4">
        <div className={`${showWideSidebar ? "main-content-large-width" : "main-content-small-width"}`}>
          <div>
            <h2 className="fw-bold">Alexandria User Guide</h2>
            <p className="text-muted">Welcome to Alexandria – your AI-powered platform for navigating knowledge, reviewing conversations, and analyzing support interactions.</p>
          </div>

          <Card className="mt-4">
            <div className="mb-4">
              <h4 className="text-purple">Login & Access</h4>
              <p>URL: <a href="https://alexandria.momentumamp.com" target="_blank" rel="noopener noreferrer">https://alexandria.momentumamp.com</a></p>
              <p className="body-text1">Access is secured via SSO for Nowcerts/MomentumAMP users. You'll land directly on the chat page after login.</p>
            </div>

            <Accordion className=" Accordion-tab">
              <AccordionTab header="Platform Overview">
                <div className="p-3">
                  <h5 className="text-purple header-text">Navigation</h5>
                  <ul className="body-text1">
                    <li>Collapsible Left Navigation Bar: Click the menu icon to expand or collapse the sidebar for a more focused view.</li>
                    <li>Logout Option: Accessible from the sidebar to securely exit the platform.</li>
                  </ul>
                </div>
              </AccordionTab>

              <AccordionTab header="Chat Interface">
                <div className="p-3">
                  <a href="https://alexandria.momentumamp.com/chat" 
                      onClick={e => {e.preventDefault(); 
                      navigate('/chat');}}
                  >
                     URL: https://alexandria.momentumamp.com/chat
                  </a>
                  <h6 className="mt-3 text-purple header-text">Ask Questions</h6>
                  <ul className="body-text1">
                    <li>Use the input at the bottom (max 8000 characters) to ask any question.</li>
                    <li>Or, click one of the suggested question cards on the landing page to start a conversation.</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">AI Responses</h6>
                  <ul className="body-text1">
                    <li>Displays your question and a markdown-formatted response.</li>
                    <li>Responses may include:
                      <ul>
                        <li>Linked documents</li>
                        <li>Related videos</li>
                        <li>Follow-up questions (3 suggestions)</li>
                      </ul>
                    </li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">Chat Actions</h6>
                  <p>For every question and response, you can:</p>
                  <ul className="body-text1">
                    <li>Bookmark – Save it for later. View in the Saved section.</li>
                    <li>Like / Dislike – Provide feedback to help improve Alexandria's answers.</li>
                    <li>Copy – Copies the plain-text version of the answer (without markdown formatting).</li>
                  </ul>
                </div>
              </AccordionTab>

              <AccordionTab header="Bookmarks">
                <div className="p-3">
                  <a href="https://alexandria.momentumamp.com/saved" 
                      onClick={e => {e.preventDefault(); 
                      navigate('/saved');}}
                  >
                     URL: https://alexandria.momentumamp.com/saved
                  </a>
                  <h6 className="mt-3 text-purple header-text">View Bookmarked Conversations</h6>
                  <ul className="body-text1">
                    <li>Shows saved Q&A in chronological order (newest at top).</li>
                    <li>Each bookmark displays:
                      <ul>
                        <li>Question</li>
                        {/* <li>Preview of the answer</li> */}
                        <li>Date/time</li>
                        <li>Bookmark ID</li>
                      </ul>
                    </li>
                    <li>Click a bookmark to reopen the full chat and continue the conversation.</li>

                  {/* <h6 className="mt-3">Remove Bookmarks</h6> */}
                  <li>Click the remove icon to delete a bookmark from your saved list.</li>
                  </ul>
                </div>
              </AccordionTab>

              <AccordionTab header="Documents">
                <div className="p-3">
                   <a href="https://alexandria.momentumamp.com/documents" 
                      onClick={e => {e.preventDefault(); 
                      navigate('/documents');}}
                  >
                     URL: https://alexandria.momentumamp.com/documents
                  </a>
                  <h6 className="mt-3 text-purple header-text">Document Types</h6>
                  <ul className="body-text1">
                    <li>Zoom calls</li>
                    <li>Fireflies transcripts</li>
                    <li>Loom recordings</li>
                    <li>PDFs and other files</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">Display Fields</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr className="text-purple header-text">
                          <th>Field</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody className="body-text">
                        <tr>
                          <td>Sr. No</td>
                          <td>Row number</td>
                        </tr>
                        <tr>
                          <td>Document Name</td>
                          <td>The uploaded file's name</td>
                        </tr>
                        <tr>
                          <td>Type</td>
                          <td>Document source (Zoom, Fireflies, Loom, Other)</td>
                        </tr>
                        <tr>
                          <td>Tag</td>
                          <td>Use tags like Support, Demo, Meeting</td>
                        </tr>
                        <tr>
                          <td>Last Modified</td>
                          <td>Format: DD-MM-YYYY HH:MM (military time)</td>
                        </tr>
                        <tr>
                          <td>Status</td>
                          <td>Draft, Approved, or Rejected</td>
                        </tr>
                        <tr>
                          <td>Actions</td>
                          <td>Edit, Preview, Delete</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h6 className="mt-3 text-purple header-text">Document Actions</h6>
                  <ul className="body-text1">
                    <li>Edit – Update metadata or replace the file.</li>
                    <li>Preview – See call summary (caller, duration, etc.), or view PDF inline.</li>
                    <li>Delete – Remove document from metadata.</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">Uploading Documents</h6>
                  <p>Use the "Upload" button to add new files. You'll be prompted to enter:</p>
                  <ul className="body-text1">
                    <li>Name</li>
                    <li>Description</li>
                    <li>File</li>
                    <li>Tag</li>
                    <li>Type</li>
                    <li>Status</li>
                  </ul>
                  <p className="text-purple header-text">Use the search bar to filter documents.</p>
                </div>
              </AccordionTab>

              <AccordionTab header="Call Analysis">
                <div className="p-3">
                    <a href="https://alexandria.momentumamp.com/supportcall" 
                      onClick={e => {e.preventDefault(); 
                      navigate('/supportcall');}}
                  >
                     URL: https://alexandria.momentumamp.com/supportcall
                  </a>
                  
                  <h6 className="mt-3 text-purple header-text">Filters (defaults to last 7 days)</h6>
                  <ul className="body-text1">
                    <li>Date From / To</li>
                    <li>Team</li>
                    <li>User Name</li>
                    <li>Direction (Inbound/Outbound)</li>
                    <li>Tag</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">Views</h6>
                  <p>You can toggle between:</p>
                  <ul className="body-text1">
                    <li>List View (tabular format)</li>
                    <li>Card View (summary cards)</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">List/Card Fields</h6>
                  <ul className="body-text1">
                    <li>User Name</li>
                    <li>Call Occurred (DD/MM/YY, HH:MM)</li>
                    <li>Call With</li>
                    <li>Team</li>
                    <li>Direction</li>
                    <li>Analysis Score (out of 10)</li>
                    <li>Tag</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">Call Analysis Details</h6>
                  <p className="body-text1">Clicking on a call entry shows detailed insights:</p>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="text-purple header-text">
                        <tr>
                          <th>Section</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody className="body-text">
                        <tr>
                          <td>Analysis Summary</td>
                          <td>Overall conversation summary</td>
                        </tr>
                        <tr>
                          <td>Customer Issue</td>
                          <td>Core problem described by the customer</td>
                        </tr>
                        <tr>
                          <td>Desired Resolution</td>
                          <td>What the customer aimed to achieve</td>
                        </tr>
                        <tr>
                          <td>Key Highlights</td>
                          <td>Notable conversation moments</td>
                        </tr>
                        <tr>
                          <td>Actionable Recommendations</td>
                          <td>Suggestions for improving support</td>
                        </tr>
                        <tr>
                          <td>Conversation Flow</td>
                          <td>Assessment of conversation pacing</td>
                        </tr>
                        <tr>
                          <td>Engagement Level</td>
                          <td>Customer participation rating</td>
                        </tr>
                        <tr>
                          <td>Resolution Effectiveness</td>
                          <td>How well the issue was addressed</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-purple header-text">You can also generate a PDF of this data.</p>
                </div>
              </AccordionTab>
               <AccordionTab header="Webinar">
                <div className="p-3">
                     <a href="https://alexandria.momentumamp.com/webinar" 
                      onClick={e => {e.preventDefault(); 
                      navigate('/webinar');}}
                  >
                     URL: https://alexandria.momentumamp.com/webinar
                  </a>
                  
                  <h6 className="mt-3 text-purple header-text">Filters (defaults to last 15 days)</h6>
                  <ul className="body-text1">
                    <li>Date From / To</li>
                    <li>Title</li>
                    <li>Host Name</li>
                    <li>Location</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">Views</h6>
                  <p>You can toggle between:</p>
                  <ul className="body-text1">
                    <li>List View (tabular format)</li>
                    <li>Card View (summary cards)</li>
                  </ul>

                  <h6 className="mt-3 text-purple header-text">List/Card Fields</h6>
                  <ul className="body-text1">
                    <li>Sr.No.</li>
                    <li>Title</li>
                    <li>Host</li>
                    <li>Team (DD/MM/YY, HH:MM)</li>
                    <li>Duration</li>
                  </ul>

                <h6 className="mt-3 text-purple header-text">Webinar</h6>
                 <p className="body-text1">Clicking on a webinar entry shows detailed insights:</p>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="text-purple header-text">
                        <tr>
                          <th>Section</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody className="body-text">
                        <tr>
                          <td>Analysis Summary</td>
                          <td>Overall conversation summary</td>
                        </tr>
                        <tr>
                          <td>Title</td>
                          <td>Title of webinar</td>
                        </tr>
                        <tr>
                          <td>Start Time</td>
                          <td>What time webinar started</td>
                        </tr>
                        <tr>
                          <td>Duration Minutes</td>
                          <td>Describes the duration of webinar</td>
                        </tr>
                        <tr>
                          <td>Host Email</td>
                          <td>Shows hosts mail</td>
                        </tr>
                        <tr>
                          <td>Participants</td>
                          <td>Shows the participants who attended the webinars</td>
                        </tr>
                        <tr>
                          <td>Chat Log</td>
                          <td>Shows the logs of chat</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-purple header-text"> On clicking on transcript you can see transcript of webinar</p>
                   <p className="text-purple header-text">On clicking on Recording you can see recorded video of webinar</p>
                </div>
              </AccordionTab>

              <AccordionTab header="Tips & Notes">
                <div className="p-3">
                  <ul className="body-text1">
                    <li>Most pages update automatically via n8n-driven API calls.</li>
                    <li>If something doesn't load, try refreshing or checking your network.</li>
                    <li>Feedback buttons help improve Alexandria—please use them!</li>
                  </ul>
                </div>
              </AccordionTab>
            </Accordion>
          </Card>
        </div>
      </div>
    </div>
  );
} 
